import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const STATUS_COLORS = {
  pending: '#f59e0b', confirmed: '#3b82f6', processing: '#8b5cf6',
  shipped: '#0891b2', delivered: '#10b981', cancelled: '#ef4444',
  on_hold: '#f97316', revised: '#6366f1',
};

export default function CustomerPortal() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [client, setClient] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    axios.get(`${API_BASE}/portal/${token}`)
      .then(r => {
        setClient(r.data.client);
        setOrders(r.data.orders || []);
      })
      .catch(e => {
        setError(e.response?.data?.error || 'Invalid or expired portal link.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, border: '3px solid #dbeafe', borderTopColor: '#1d4ed8', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
        <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading your portal…</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
      <div style={{ textAlign: 'center', maxWidth: 400, padding: 32 }}>
        <div style={{ fontSize: '3rem', marginBottom: 12 }}>🔒</div>
        <h2 style={{ color: '#0f172a', marginBottom: 8 }}>Access Denied</h2>
        <p style={{ color: '#64748b' }}>{error}</p>
        <p style={{ color: '#94a3b8', fontSize: '0.82rem', marginTop: 16 }}>Please contact your PRASI LABS representative for a valid portal link.</p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f0f4f8' }}>
      {/* Header */}
      <header style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, background: '#1d4ed8', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: '1rem' }}>P</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>PRASI LABS</div>
            <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Customer Portal</div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 600, color: '#0f172a' }}>{client.name}</div>
          {client.city && <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{client.city}{client.country ? `, ${client.country}` : ''}</div>}
        </div>
      </header>

      {/* Main content */}
      <main style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
        <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Your Orders & Invoices</h2>
        <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: 20 }}>{orders.length} document{orders.length !== 1 ? 's' : ''} found</p>

        {orders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>📄</div>
            <div style={{ color: '#64748b' }}>No orders yet. Contact your representative.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {orders.map(order => (
              <div key={order.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.95rem' }}>{order.order_number}</div>
                  <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 2 }}>
                    {order.order_type?.replace(/_/g, ' ')} · {order.order_date ? new Date(order.order_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                  </div>
                </div>
                <div style={{ textAlign: 'right', minWidth: 100 }}>
                  <div style={{ fontWeight: 700, color: '#0f172a' }}>{order.currency || '₹'}{order.total ? Number(order.total).toLocaleString('en-IN') : '—'}</div>
                </div>
                <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, background: (STATUS_COLORS[order.status] || '#64748b') + '18', color: STATUS_COLORS[order.status] || '#64748b', textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                  {order.status}
                </span>
                {order.waybill_number && (
                  <a href={order.tracking_url} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: '0.78rem', color: '#0891b2', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                    Track: {order.waybill_number} →
                  </a>
                )}
                {order.payment_link_url && (
                  <a href={order.payment_link_url} target="_blank" rel="noopener noreferrer"
                    style={{ padding: '5px 12px', background: '#10b981', color: '#fff', borderRadius: 6, fontSize: '0.78rem', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                    Pay Now
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      <footer style={{ textAlign: 'center', padding: '24px 16px', color: '#94a3b8', fontSize: '0.75rem' }}>
        This is a read-only portal. For queries, contact your PRASI LABS representative.
      </footer>
    </div>
  );
}
